import { entries } from "@impero-com/common/utils/entries";
import { safeZodPath } from "@impero-com/common/utils/form/safeZodPath";
import { useTranslation, TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { z } from "zod";
import { ParsePayload } from "zod/v4/core";

import { findDependency } from "components/fieldBuilder/utils";
import {
    columnSchema,
    fileUploadFieldTypeSchema,
    getBaseFieldSchema,
    getCheckboxGroupFieldTypeSchema,
    getDataGridFieldTypeSchema as getBaseDataGridFieldTypeSchema,
    getDropdownFieldTypeSchema,
    getRadioButtonGroupFieldTypeSchema,
    headingFieldTypeSchema,
    numericInputFieldTypeSchema,
    optionSchema,
    textAreaFieldTypeSchema,
    textInputFieldTypeSchema,
} from "components/fieldBuilder/zodSchemas";

import { getConclusionFieldTypeConditionalValues } from "./fieldBuilderUtils";

export function getDataGridFieldTypeSchema(t: TFunction) {
    return getBaseDataGridFieldTypeSchema(t).extend({
        "0": columnSchema, // This is to ensure that the first column is always present
    });
}
export type DataGridFieldType = z.infer<
    ReturnType<typeof getDataGridFieldTypeSchema>
>;

export const handleDuplicateEffectivenessOptions = (
    options: EffectivenessOptions,
    onDuplicate: (key: keyof EffectivenessOptions) => void
) => {
    const optionsEntries = entries(options);
    optionsEntries.forEach(([key, option]) => {
        const isOptionUnique =
            optionsEntries.filter(([k, v]) => k !== key && v === option)
                .length === 0;
        if (!isOptionUnique) {
            onDuplicate(key);
        }
    });
};

const unrefinedEffectivenessOptionsSchema = z.object({
    effectiveOptionName: optionSchema,
    notEffectiveOptionName: optionSchema,
    partiallyEffectiveOptionName: optionSchema,
});
function getEffectivenessOptionsSchema(t: TFunction) {
    return unrefinedEffectivenessOptionsSchema.check((ctx) =>
        handleDuplicateEffectivenessOptions(ctx.value, (key) =>
            ctx.issues.push({
                message: t("FieldBuilder_Error_OptionAlreadyExists"),
                path: [key],
                code: "custom",
                input: ctx.value,
            })
        )
    );
}

export type EffectivenessOptions = z.infer<
    typeof unrefinedEffectivenessOptionsSchema
>;

function getEffectivenessFieldTypeSchema(t: TFunction) {
    return z.object({
        type: z.literal("Effectiveness"),
        options: getEffectivenessOptionsSchema(t),
    });
}
export type EffectivenessFieldType = z.infer<
    ReturnType<typeof getEffectivenessFieldTypeSchema>
>;

function getControlConclusionFieldTypeSchema(t: TFunction) {
    return z.discriminatedUnion("type", [
        getEffectivenessFieldTypeSchema(t),
        textInputFieldTypeSchema,
        textAreaFieldTypeSchema,
        numericInputFieldTypeSchema,
        getRadioButtonGroupFieldTypeSchema(t),
        getCheckboxGroupFieldTypeSchema(t),
        getDropdownFieldTypeSchema(t),
        fileUploadFieldTypeSchema,
        headingFieldTypeSchema,
    ]);
}
export type ControlConclusionFieldType = z.infer<
    ReturnType<typeof getControlConclusionFieldTypeSchema>
>;
export type ControlConclusionFieldTypeName = ControlConclusionFieldType["type"];

function getSampleConclusionFieldTypeSchema(t: TFunction) {
    return z.discriminatedUnion("type", [
        textInputFieldTypeSchema,
        textAreaFieldTypeSchema,
        numericInputFieldTypeSchema,
        getRadioButtonGroupFieldTypeSchema(t),
        getCheckboxGroupFieldTypeSchema(t),
        getDropdownFieldTypeSchema(t),
        fileUploadFieldTypeSchema,
        headingFieldTypeSchema,
    ]);
}
export type SampleConclusionFieldType = z.infer<
    ReturnType<typeof getSampleConclusionFieldTypeSchema>
>;
export type SampleConclusionFieldTypeName = SampleConclusionFieldType["type"];

export const getFieldTypeSchema = (t: TFunction) =>
    z.union([
        getControlConclusionFieldTypeSchema(t),
        getSampleConclusionFieldTypeSchema(t),
    ]);
export type ConclusionFieldTypeName = z.infer<
    ReturnType<typeof getFieldTypeSchema>
>["type"];

function getControlConclusionFieldSchema(t: TFunction) {
    return z
        .object({
            fieldType: getControlConclusionFieldTypeSchema(t),
        })
        .merge(getBaseFieldSchema(t));
}
export type ControlConclusionField = z.infer<
    ReturnType<typeof getControlConclusionFieldSchema>
>;
export type ControlConclusionFieldArray = ControlConclusionField[];

function getSampleConclusionFieldSchema(t: TFunction) {
    return z
        .object({
            fieldType: getSampleConclusionFieldTypeSchema(t),
        })
        .merge(getBaseFieldSchema(t));
}
export type SampleConclusionField = z.infer<
    ReturnType<typeof getSampleConclusionFieldSchema>
>;
export type SampleConclusionFieldArray = SampleConclusionField[];

const getFieldSchema = (t: TFunction) =>
    z.union([
        getControlConclusionFieldSchema(t),
        getSampleConclusionFieldSchema(t),
    ]);

export type ConclusionField = z.infer<ReturnType<typeof getFieldSchema>>;
export type ConclusionFieldType = ConclusionField["fieldType"];

const getUnrefinedFieldArraySchema = (t: TFunction) =>
    z.array(getFieldSchema(t));
type UnrefinedFieldArray = z.infer<
    ReturnType<typeof getUnrefinedFieldArraySchema>
>;

function validateFieldsConditions(
    fields: UnrefinedFieldArray,
    ctx: ParsePayload,
    t: TFunction
) {
    fields.forEach((field, index) => {
        const condition = field.condition;
        if (!condition) {
            return;
        }
        const dependency = findDependency(condition.fieldId, fields, field.id);
        switch (dependency.type) {
            case "FOUND": {
                break;
            }
            case "FOUND_AFTER": {
                ctx.issues.push({
                    message: t(
                        "FieldBuilder_Error_Condition_Dependency_FoundAfter"
                    ),
                    code: "custom",
                    path: safeZodPath<ConclusionFieldArray>(
                        `${index}.condition`
                    ),
                    input: ctx.value,
                });
                return;
            }
            case "NOT_FOUND": {
                ctx.issues.push({
                    message: t(
                        "FieldBuilder_Error_Condition_Dependency_NotFound"
                    ),
                    code: "custom",
                    path: safeZodPath<ConclusionFieldArray>(
                        `${index}.condition`
                    ),
                    input: ctx.value,
                });
                return;
            }
            default: {
                assertNever(dependency);
            }
        }
        const conclusionFieldType = dependency.value.fieldType;
        if (
            !(
                getConclusionFieldTypeConditionalValues(
                    conclusionFieldType
                ).findIndex((v) => v === condition.value) >= 0
            )
        ) {
            ctx.issues.push({
                message: t(
                    "FieldBuilder_Error_Condition_Dependency_ValueNotFound"
                ),
                code: "custom",
                path: safeZodPath<ConclusionFieldArray>(`${index}.condition`),
                input: ctx.value,
            });
        }
    });
}

export function useControlConclusionFieldArraySchema() {
    const { t } = useTranslation();
    return z
        .array(getControlConclusionFieldSchema(t))
        .check((ctx) => validateFieldsConditions(ctx.value, ctx, t));
}
export function useSampleConclusionFieldArraySchema() {
    const { t } = useTranslation();
    return z
        .array(getSampleConclusionFieldSchema(t))
        .check((ctx) => validateFieldsConditions(ctx.value, ctx, t));
}

export function useFieldArraySchema() {
    const { t } = useTranslation();
    return getUnrefinedFieldArraySchema(t).check((ctx) =>
        validateFieldsConditions(ctx.value, ctx, t)
    );
}
export type ConclusionFieldArray = z.infer<
    ReturnType<typeof useFieldArraySchema>
>;
